import { useState } from "react";
import { Button, Checkbox, Field, Input, MessageBar, MessageBarBody, SpinButton, Text } from "@fluentui/react-components";
import { CreateClientInput, type ClientPreferenceInput } from "@gymplan/shared";
import { PreferenceCalendar } from "./PreferenceCalendar.tsx";
import type { AvailabilitySlot } from "../lib/api.ts";

export interface ClientFormValue {
  name: string;
  phone: string;
  email: string;
  sessionsPerWeek: number;
  preferences: ClientPreferenceInput[];
}

export function ClientForm({
  initial,
  gyms,
  onSubmit,
  submitLabel = "Save",
  busy,
  extra,
  availability,
  sessionLengthMinutes,
  requireAttestation,
}: {
  initial?: Partial<ClientFormValue>;
  gyms: { id: string; name: string; isPrimary: boolean }[];
  onSubmit: (v: CreateClientInput) => Promise<void>;
  submitLabel?: string;
  busy?: boolean;
  /** Extra fields rendered before the submit button (e.g. password on self-signup) */
  extra?: React.ReactNode;
  /** Trainer's weekly availability, drawn under the preference grid */
  availability?: AvailabilitySlot[];
  sessionLengthMinutes?: number;
  /** Trainer adding someone else: show the consent attestation (required) */
  requireAttestation?: boolean;
}) {
  const [f, setF] = useState<ClientFormValue>({
    name: initial?.name ?? "",
    phone: initial?.phone ?? "",
    email: initial?.email ?? "",
    sessionsPerWeek: initial?.sessionsPerWeek ?? 2,
    preferences: initial?.preferences ?? [],
  });
  const [attested, setAttested] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErr(null);
    const parsed = CreateClientInput.safeParse({ ...f, email: f.email || undefined, consentAttested: requireAttestation ? attested : undefined });
    if (!parsed.success) {
      setErr(parsed.error.issues.map((i) => `${i.path.join(".") || "form"}: ${i.message}`).join(" · "));
      return;
    }
    try {
      await onSubmit(parsed.data);
    } catch (ex) {
      setErr((ex as Error).message);
    }
  };

  return (
    <form onSubmit={submit} style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      {err && (
        <MessageBar intent="error">
          <MessageBarBody>{err}</MessageBarBody>
        </MessageBar>
      )}
      <Field label="Name" required>
        <Input value={f.name} onChange={(_, d) => setF({ ...f, name: d.value })} />
      </Field>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
        <Field label="WhatsApp number" required hint="e.g. +447700900123">
          <Input type="tel" value={f.phone} onChange={(_, d) => setF({ ...f, phone: d.value.replace(/\s+/g, "") })} />
        </Field>
        <Field label="Email (optional)">
          <Input type="email" value={f.email} onChange={(_, d) => setF({ ...f, email: d.value })} />
        </Field>
      </div>
      <Field label="Sessions per week">
        <SpinButton min={1} max={14} value={f.sessionsPerWeek} onChange={(_, d) => setF({ ...f, sessionsPerWeek: d.value ?? f.sessionsPerWeek })} style={{ maxWidth: 120 }} />
      </Field>
      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
        <Text weight="semibold" size={300}>Preferred training times</Text>
        <PreferenceCalendar value={f.preferences} onChange={(preferences) => setF({ ...f, preferences })} gyms={gyms} availability={availability} sessionLengthMinutes={sessionLengthMinutes} />
      </div>
      {extra}
      {requireAttestation && (
        <Checkbox
          checked={attested}
          onChange={(_, d) => setAttested(!!d.checked)}
          label="This person has agreed to be added, to receive WhatsApp messages from RackUp on my behalf, and is 18 or over. They'll get a message with the privacy notice and how to opt out."
        />
      )}
      <Button appearance="primary" type="submit" disabled={busy || (requireAttestation && !attested)} style={{ alignSelf: "flex-start" }}>
        {submitLabel}
      </Button>
    </form>
  );
}
